'use client'

import { useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowRight01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

type DiffRowProps = {
  field: string
  oldValue: unknown
  newValue: unknown
  checked: boolean
  onToggle: () => void
}

const enumLabels: Record<string, string> = {
  sparkles: 'Sparkles',
  shield: 'Shield',
  balance: 'Balance',
  pause: 'Pause',
  filter: 'Filter',
  users: 'Users',
  sliders: 'Sliders',
  route: 'Route',
  clock: 'Clock',
  activity: 'Activity',
  binoculars: 'Binoculars',
  file: 'File',
  workspace: 'Workspace',
  search: 'Search',
  social: 'Social',
  tv: 'TV',
  programmatic: 'Programmatic',
  audio: 'Audio',
  retail: 'Retail',
  ai: 'AI',
  deterministic: 'Workflow',
  manual: 'Manual',
  schedule: 'Scheduled',
  event: 'Event',
  chat: 'Chat',
  auto: 'Auto',
}

export function DiffRow({ field, oldValue, newValue, checked, onToggle }: DiffRowProps) {
  const [expanded, setExpanded] = useState(false)
  const display = renderDiff(field, oldValue, newValue)

  return (
    <div className={cn('grid gap-3 rounded-lg border border-atrium-border px-3 py-2 sm:grid-cols-[24px_140px_1fr]', !checked && 'opacity-60')}>
      <Checkbox checked={checked} onCheckedChange={onToggle} aria-label={`Apply ${field}`} />
      <div className="font-mono text-xs text-atrium-black/60">{field}</div>
      <div className="min-w-0 text-sm text-atrium-black">
        {display.kind === 'graph' ? (
          <div className="space-y-2">
            <button type="button" className="text-left text-atrium-black underline-offset-2 hover:underline" onClick={() => setExpanded((open) => !open)}>
              {display.summary}
            </button>
            {expanded && <pre className="max-h-56 overflow-auto rounded-lg bg-atrium-content p-3 text-xs">{JSON.stringify(newValue, null, 2)}</pre>}
          </div>
        ) : display.kind === 'transition' ? (
          <div className="flex min-w-0 items-center gap-2">
            <span className="truncate text-atrium-black/45">{display.oldText}</span>
            <HugeiconsIcon icon={ArrowRight01Icon} className="size-3.5 shrink-0 text-atrium-black/35" />
            <span className="truncate font-medium">{display.newText}</span>
          </div>
        ) : (
          <span>{display.text}</span>
        )}
      </div>
    </div>
  )
}

function renderDiff(field: string, oldValue: unknown, newValue: unknown):
  | { kind: 'transition'; oldText: string; newText: string }
  | { kind: 'text'; text: string }
  | { kind: 'graph'; summary: string } {
  if (field === 'allowed_tools') {
    const oldTools = Array.isArray(oldValue) ? oldValue.map(String) : []
    const newTools = Array.isArray(newValue) ? newValue.map(String) : []
    const added = newTools.filter((tool) => !oldTools.includes(tool))
    const removed = oldTools.filter((tool) => !newTools.includes(tool))
    const parts = [
      added.length > 0 ? `+${added.length}: ${truncateList(added)}` : null,
      removed.length > 0 ? `-${removed.length}: ${truncateList(removed)}` : null,
    ].filter(Boolean)
    return { kind: 'text', text: parts.join('  ') || `${newTools.length} selected` }
  }

  if (field === 'trigger_config') {
    return { kind: 'transition', oldText: describeTriggerConfig(oldValue), newText: describeTriggerConfig(newValue) }
  }

  if (field === 'deterministic_graph') {
    return { kind: 'graph', summary: summarizeGraph(newValue) }
  }

  if (['icon_key', 'channel', 'execution_mode', 'model_key', 'trigger_type'].includes(field)) {
    return { kind: 'transition', oldText: enumLabel(oldValue), newText: enumLabel(newValue) }
  }

  return { kind: 'transition', oldText: truncateValue(oldValue), newText: truncateValue(newValue) }
}

function truncateValue(value: unknown) {
  const text = typeof value === 'string' ? value : value == null ? 'empty' : JSON.stringify(value)
  if (!text) return 'empty'
  return text.length > 60 ? `${text.slice(0, 60)}...` : text
}

function enumLabel(value: unknown) {
  const key = String(value ?? '')
  return enumLabels[key] ? `${enumLabels[key]} (${key})` : key || 'empty'
}

function truncateList(values: string[]) {
  const joined = values.slice(0, 4).join(', ')
  return values.length > 4 ? `${joined}, ...` : joined
}

function describeTriggerConfig(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'empty'
  const config = value as Record<string, unknown>
  if (config.cadence === 'preset') return `Preset: ${String(config.preset || 'weekday_morning').replace(/_/g, ' ')}`
  if (config.cadence === 'cron') return `Cron: ${String(config.expr || '')} ${String(config.timezone || '')}`.trim()
  if (config.cadence === 'interval') return `Every ${String(config.minutes || 1)} minutes`
  if (config.event_type) return `Event: ${String(config.event_type)}`
  return JSON.stringify(config)
}

function summarizeGraph(value: unknown) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 'No graph'
  const nodes = Array.isArray((value as Record<string, unknown>).nodes) ? (value as { nodes: unknown[] }).nodes : []
  const kinds = nodes
    .map((node) => node && typeof node === 'object' && !Array.isArray(node) ? String((node as Record<string, unknown>).kind || '') : '')
    .filter(Boolean)
  return `${nodes.length}-step graph: ${kinds.slice(0, 6).join(' -> ')}${kinds.length > 6 ? ' -> ...' : ''}`
}
